import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, LoginCredentials, RegisterData } from '@/types/auth'
import { authApi } from '@/api/auth'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const isLoading = ref<boolean>(false)

  const isAuthenticated = computed((): boolean => {
    return !!token.value && !!user.value
  })

  const initializeAuth = (): void => {
    const storedToken = localStorage.getItem('auth_token')
    const storedUser = localStorage.getItem('user')

    if (storedToken) {
      token.value = storedToken
    }

    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser)
      } catch (error) {
        localStorage.removeItem('user')
      }
    }
  }

  const login = async (credentials: LoginCredentials): Promise<void> => {
    isLoading.value = true
    try {
      const tokenResponse = await authApi.login(credentials)
      token.value = tokenResponse.access_token

      localStorage.setItem('auth_token', tokenResponse.access_token)

      const currentUser = await authApi.getCurrentUser()
      user.value = currentUser
      localStorage.setItem('user', JSON.stringify(currentUser))
    } finally {
      isLoading.value = false
    }
  }

  const register = async (data: RegisterData): Promise<void> => {
    isLoading.value = true
    try {
      const newUser = await authApi.register(data)
      user.value = newUser
    } finally {
      isLoading.value = false
    }
  }

  const logout = (): void => {
    user.value = null
    token.value = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
  }

  const refreshUser = async (): Promise<void> => {
    if (!token.value) return

    try {
      const currentUser = await authApi.getCurrentUser()
      user.value = currentUser
      localStorage.setItem('user', JSON.stringify(currentUser))
    } catch (error) {
      await logout()
    }
  }

  return {
    user,
    token,
    isLoading,
    isAuthenticated,
    initializeAuth,
    login,
    register,
    logout,
    refreshUser
  }
})
